import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import './Map.css';

const containerStyle = {
    width: '100%',
    height: '100%',
};

const defaultCenter = {
    lat: 24.877466,
    lng: 121.247505,
};

const TYPE_COLORS = {
    SIS: '#90AE81',
    RA: '#5AA7C5',
    ALC: 'rgb(228, 181, 52)',
    OW: 'rgb(221, 93, 81)',
    TI: 'rgb(158, 157, 157)',
    default: '#2e7d32',
};

const STATUS_COLORS = {
    green: '#3ECD5B',
    yellow: '#FACC15',
    red: '#F87171',
    warning: '#ed6c02',
    error: '#d32f2f',
    default: '#d1d5db',
};

const mapOptions = {
    clickableIcons: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: true,
    mapTypeId: 'terrain',
    styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
};

const statusMessage = (message) => <div className="map__status">{message}</div>;

const TAIL_VARIANTS = [60, 80, 100];

const CustomMarker = ({ text, type, status, onClick, tailLength = TAIL_VARIANTS[0] }) => {
    const mainColor = TYPE_COLORS[type] || TYPE_COLORS.default;
    const statusColor = STATUS_COLORS[status] || STATUS_COLORS.default;
    const pointerY = Math.max(tailLength - 2, 12);

    const styles = {
        container: {
            position: 'absolute',
            display: 'flex',
            alignItems: 'flex-start',
            transform: 'translate(-100%, -100%)',
            pointerEvents: 'none',
        },
        button: {
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            pointerEvents: 'auto',
        },
        label: {
            backgroundColor: mainColor,
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '2px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.2)',
            fontWeight: 'bold',
            marginTop: 0,
            position: 'relative',
            zIndex: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
        },
        statusDot: {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: statusColor,
            border: '1px solid rgba(0,0,0,0.2)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        },
        svgBox: {
            width: '40px',
            height: `${tailLength}px`,
            marginLeft: '-1px',
            overflow: 'visible',
            zIndex: 1,
            pointerEvents: 'none',
        },
    };

    return (
        <div style={styles.container}>
            <button type="button" style={styles.button} onClick={onClick}>
                <div style={styles.label}>
                    <span>{text}</span>
                    <span style={styles.statusDot} aria-label={`status-${status}`} />
                </div>
            </button>
            <svg style={styles.svgBox} viewBox={`0 0 40 ${tailLength}`}>
                <path
                    d={`M 0 12 L 15 12 L 38 ${pointerY}`}
                    fill="none"
                    stroke={mainColor}
                    strokeWidth="2"
                />
                <circle
                    cx="38"
                    cy={pointerY}
                    r="3"
                    fill="white"
                    stroke={mainColor}
                    strokeWidth="2"
                />
            </svg>
        </div>
    );
};

const Map = ({ apiKey, center = defaultCenter, markers = [] }) => {
    const [zoom, setZoom] = useState(11);
    const mapRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: apiKey,
    });

    const onLoad = useCallback((map) => {
        mapRef.current = map;
        setZoom(map.getZoom());
    }, []);

    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    const onZoomChanged = useCallback(() => {
        if (mapRef.current) {
            setZoom(mapRef.current.getZoom());
        }
    }, []);

    if (!apiKey) return statusMessage('缺少 Google Maps API Key');
    if (loadError) return statusMessage('地圖載入失敗，請稍後重試');
    if (!isLoaded) return statusMessage('地圖載入中…');

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onZoomChanged={onZoomChanged}
            options={mapOptions}
        >
            {markers.map((marker) => (
                <OverlayView
                    key={marker.id}
                    position={marker.position}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                    <CustomMarker
                        text={marker.name}
                        type={marker.type}
                        status={marker.status}
                        tailLength={marker.tailLength}
                        onClick={(event) => {
                            event?.stopPropagation?.();
                            if (marker.onClick) {
                                marker.onClick(marker);
                            }
                        }}
                    />
                </OverlayView>
            ))}
        </GoogleMap>
    );
};

export default Map;
